import { WritableObservable } from "micro-observables";
import { LocalStorage } from "@core/localStorage/storage";
import { PaginatedStore } from "@core/store/paginatedStore";
import { StorePresentation } from "@core/store/storePresentation";
import { PaginationOptions } from "@utils/page";
import { Carousel } from "@domain/carousel/carousel";
import { CarouselService } from "@domain/carousel/carouselService";
import { CountryCode } from "@domain/currentProfile/countryCode";
import { Group } from "@domain/group/group";
import { GroupService } from "@domain/group/groupService";
import { ContentLicense } from "@domain/licence/contentLicense";
import { Article } from "@domain/lms/article/article";
import { ArticleService } from "@domain/lms/article/articleService";
import { Category } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentLevel } from "@domain/lms/contentLevel";
import { Course, CourseDuration, CourseProvider } from "@domain/lms/course/course";
import { CourseService } from "@domain/lms/course/courseService";
import { Quiz } from "@domain/lms/quiz/quiz";
import { QuizService } from "@domain/lms/quiz/quizService";
import { Organization } from "@domain/organization/organization";
import { OrganizationService } from "@domain/organization/organizationService";
import { OrganizationSubType, OrganizationType } from "@domain/organization/organizationType";
import { Scoring } from "@domain/ranking/ranking";
import { Profile } from "@domain/user/profile";
import { UserService } from "@domain/user/userService";
import { SearchDomain } from "./searchDomain";

export type SearchType = "search" | "suggestion";

export type SearchArguments = [
	string,
	Category[]?,
	ContentLevel[]?,
	ContentLanguage[]?,
	CourseDuration[]?,
	CourseProvider[]?,
	OrganizationType[]?,
	OrganizationSubType[]?,
	CountryCode[]?,
	ContentLicense[]?,
	Scoring[]?,
	number?,
];

export class SearchService {
	domainProviders = {
		[SearchDomain.Course]: {
			store: this.courseService.courseStore,
			search: this.courseService.search,
			suggest: this.courseService.suggest,
		},
		[SearchDomain.Article]: {
			store: this.articleService.articleStore,
			search: this.articleService.search,
			suggest: (count: number) => this.articleService.suggest(count),
		},
		[SearchDomain.Quiz]: {
			store: this.quizService.quizStore,
			search: this.quizService.search,
			suggest: this.quizService.suggest,
		},
		[SearchDomain.User]: {
			store: this.userService.profilesStore,
			search: this.userService.search,
			suggest: this.userService.suggest,
		},
		[SearchDomain.Organization]: {
			store: this.organizationService.organizationStore,
			search: this.organizationService.search,
			suggest: this.organizationService.suggest,
		},
		[SearchDomain.Shareroom]: {
			store: this.groupService.groupStore,
			search: (page: PaginationOptions, ...searchParams: SearchArguments) =>
				this.groupService.search("SHAREROOM", page, ...searchParams),
			suggest: (count: number) => this.groupService.suggest(count, "SHAREROOM"),
		},
		[SearchDomain.Discussion]: {
			store: this.groupService.groupStore,
			search: (page: PaginationOptions, ...searchParams: SearchArguments) =>
				this.groupService.search("DISCUSSION_GROUP", page, ...searchParams),
			suggest: (count: number) => this.groupService.suggest(count, "DISCUSSION_GROUP"),
		},
		[SearchDomain.Carousel]: {
			store: this.carouselService.carouselStore,
			search: this.carouselService.search,
			suggest: this.organizationService.suggest,
		},
	};

	stores: {
		[key in SearchDomain]:
			| PaginatedStore<Course, unknown, SearchArguments>
			| PaginatedStore<Article, unknown, SearchArguments>
			| PaginatedStore<Quiz, unknown, SearchArguments>
			| PaginatedStore<Profile, unknown, SearchArguments>
			| PaginatedStore<Organization, unknown, SearchArguments>
			| PaginatedStore<Carousel, unknown, SearchArguments>
			| PaginatedStore<Group, unknown, SearchArguments>;
	} = {
		[SearchDomain.Course]: new StorePresentation<Course, SearchArguments>(
			this.domainProviders[SearchDomain.Course].store,
			this.domainProviders[SearchDomain.Course].search
		),
		[SearchDomain.Article]: new StorePresentation<Article, SearchArguments>(
			this.domainProviders[SearchDomain.Article].store,
			this.domainProviders[SearchDomain.Article].search
		),
		[SearchDomain.Quiz]: new StorePresentation<Quiz, SearchArguments>(
			this.domainProviders[SearchDomain.Quiz].store,
			this.domainProviders[SearchDomain.Quiz].search
		),
		[SearchDomain.User]: new StorePresentation<Profile, SearchArguments>(
			this.domainProviders[SearchDomain.User].store,
			this.domainProviders[SearchDomain.User].search
		),
		[SearchDomain.Organization]: new StorePresentation<Organization, SearchArguments>(
			this.domainProviders[SearchDomain.Organization].store,
			this.domainProviders[SearchDomain.Organization].search
		),
		[SearchDomain.Shareroom]: new StorePresentation<Group, SearchArguments>(
			this.domainProviders[SearchDomain.Shareroom].store,
			this.domainProviders[SearchDomain.Shareroom].search
		),
		[SearchDomain.Discussion]: new StorePresentation<Group, SearchArguments>(
			this.domainProviders[SearchDomain.Discussion].store,
			this.domainProviders[SearchDomain.Discussion].search
		),
		[SearchDomain.Carousel]: new StorePresentation<Carousel, SearchArguments>(
			this.domainProviders[SearchDomain.Carousel].store,
			this.domainProviders[SearchDomain.Carousel].search
		),
	};

	lastSearches = new WritableObservable(this.queryStorage?.getItem([]));

	constructor(
		private readonly userService: UserService,
		private readonly courseService: CourseService,
		private readonly articleService: ArticleService,
		private readonly quizService: QuizService,
		private readonly organizationService: OrganizationService,
		private readonly groupService: GroupService,
		private readonly carouselService: CarouselService,
		private readonly queryStorage: LocalStorage<string[]>
	) {
		for (const domain of Object.keys(this.stores)) {
			const store = this.stores[domain as SearchDomain];
			store.onFetch.add(({ args: [query], result }: { args: SearchArguments; result: { total_size: number } }) => {
				if (!!query && result.total_size > 0) {
					const currentQueries = this.queryStorage?.getItem([]);
					if (currentQueries.indexOf(query) < 0) {
						const newQueries = [query, ...(currentQueries.length < 5 ? currentQueries : currentQueries.slice(0, 4))];
						this.queryStorage.setItem(newQueries);
						this.lastSearches.set(newQueries);
					}
				}
			});
		}
	}
}
